import { prefetchBrowsePreset } from '@aibees/shared/api';
import { useWorkspace } from '@aibees/shared/workspace';
import { useEffect } from 'react';
import {
  MORE_FILTERS_ORDER_PRESET_TYPE,
  MORE_FILTERS_PRESET_TYPE,
} from '@aibees/elephant/feature/more-filters/constants';
import { useSearchTableScopeFromRoute } from '@aibees/elephant/context/search-table-scope';
import { ElephantPresetType } from '@aibees/elephant/types';

const LAYOUT_PREFETCH_DELAY_MS = 50;

/**
 * Prefetches preset APIs used by the search page so that when PinnedFiltersProvider
 * and TableLayoutTemplateProvider mount they get cache hits, reducing parallel requests.
 * Filter presets are prefetched first; layout presets are prefetched after a short delay.
 */
export function usePrefetchSearchPresets() {
  const workspaceId = useWorkspace()?.id;
  const scope = useSearchTableScopeFromRoute();

  useEffect(() => {
    if (!workspaceId) return;

    const run = async () => {
      try {
        await Promise.all([
          prefetchBrowsePreset(workspaceId, {
            type: MORE_FILTERS_PRESET_TYPE,
            name: MORE_FILTERS_PRESET_TYPE,
            size: 1,
          }),
          prefetchBrowsePreset(workspaceId, {
            type: MORE_FILTERS_ORDER_PRESET_TYPE,
            name: MORE_FILTERS_ORDER_PRESET_TYPE,
            size: 1,
          }),
        ]);
      } catch {
        // Prefetch is best-effort; consumers will refetch on mount if needed
      }

      const layoutDelay = new Promise<void>((resolve) =>
        setTimeout(resolve, LAYOUT_PREFETCH_DELAY_MS)
      );
      await layoutDelay;

      if (scope === 'company') {
        try {
          await Promise.all([
            prefetchBrowsePreset(workspaceId, {
              type: 'SELECTED_'.concat(
                ElephantPresetType.COMPANY_SEARCH_TABLE_LAYOUT
              ),
              page: 0,
              size: 1,
            }),
            prefetchBrowsePreset(workspaceId, {
              type: ElephantPresetType.COMPANY_SEARCH_TABLE_LAYOUT,
              size: 25,
            }),
          ]);
        } catch {
          // best-effort
        }
      } else if (scope === 'people') {
        try {
          await Promise.all([
            prefetchBrowsePreset(workspaceId, {
              type: 'SELECTED_'.concat(
                ElephantPresetType.PEOPLE_SEARCH_TABLE_LAYOUT
              ),
              page: 0,
              size: 1,
            }),
            prefetchBrowsePreset(workspaceId, {
              type: ElephantPresetType.PEOPLE_SEARCH_TABLE_LAYOUT,
              size: 25,
            }),
          ]);
        } catch {
          // best-effort
        }
      }
    };

    run();
  }, [workspaceId, scope]);
}
